import { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

import { CustomerPortal } from './CustomerPortal';
import { useDialog } from './Dialog';
import { SubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { capitalize, getCreditsForTier } from '../config/pricingConfig';
import { useMsal } from 'src/auth/firebaseMsalCompat';
import { loginRequest } from '../config/authConfig';
import { getApiHost } from '../utils/getApiHost';
import { trackButtonClick } from '../services/telemetry';

interface SubscriptionStatusCardProps {
  subscriptionStatus: SubscriptionStatus;
  /** Telemetry source label (e.g. 'Profile'). */
  source?: string;
}

/** Yellow is the remaining allowance and shrinks from the right as credits are used. */
const MonthlyCreditBar = ({
  balance,
  total,
}: {
  balance: number;
  total: number;
}) => {
  const fraction = total > 0 ? balance / total : 0;

  return (
    <div className="mt-5">
      <p
        className={`
          mb-1 flex items-center justify-between text-sm text-gray-700
        `}
      >
        <span>Monthly Credit Available</span>
        <span className="font-semibold text-gray-800">
          {balance} / {total}
        </span>
      </p>
      <div
        className="h-5 overflow-hidden rounded-[7px] bg-[#F5F5F6]"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={balance}
        aria-label={`Monthly Credit Available: ${balance} of ${total}`}
      >
        <div
          className="h-full bg-[#FFEC83]"
          style={{ width: `${Math.min(fraction * 100, 100)}%` }}
        />
      </div>
      <p className="mt-1 text-sm text-gray-400">
        Credits will reset at your next billing cycle.
      </p>
    </div>
  );
};

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

/** Business API trial end date and job credits, when the account has them. */
const BusinessApiLimits = ({
  subscriptionStatus,
}: {
  subscriptionStatus: SubscriptionStatus;
}) => {
  const trialEndsAt = subscriptionStatus.businessTrialEndsAt;
  const limit = subscriptionStatus.businessCreditLimit;
  const used = subscriptionStatus.businessCreditsUsed ?? 0;
  if (!trialEndsAt && (limit === null || limit === undefined)) return null;

  const trialEnded = !!trialEndsAt && new Date(trialEndsAt) <= new Date();
  return (
    <div className="mt-5 space-y-1 text-sm text-gray-700">
      {trialEndsAt && (
        <p className="flex items-center justify-between">
          <span>{trialEnded ? 'API trial ended' : 'API trial ends'}</span>
          <span
            className={`font-semibold ${trialEnded ? 'text-red-600' : 'text-gray-800'}`}
          >
            {formatDate(trialEndsAt)}
          </span>
        </p>
      )}
      {limit !== null && limit !== undefined && (
        <p className="flex items-center justify-between">
          <span>API jobs remaining</span>
          <span className="font-semibold text-gray-800">
            {Math.max(limit - used, 0)} of {limit}
          </span>
        </p>
      )}
    </div>
  );
};

/**
 * Self-contained subscription status display: current plan, billing period,
 * credits, scheduled plan change (with cancel), past-due warning, plus
 * "Change / Upgrade plan" and the Stripe Customer Portal.
 */
export const SubscriptionStatusCard = ({
  subscriptionStatus,
  source = 'Profile',
}: SubscriptionStatusCardProps) => {
  const { instance } = useMsal();
  const { showAlert, showConfirm, DialogComponent } = useDialog();
  const [isLoading, setIsLoading] = useState(false);

  const tier = subscriptionStatus.subscriptionTier || 'free';
  // Business billing is invoiced usage, tracked via userType rather than a
  // Starter/Pro tier. Don't treat those accounts as the free plan.
  const isBusiness = subscriptionStatus.userType === 'business';
  const isPaid = !isBusiness && (tier === 'starter' || tier === 'pro');
  const planLabel = isBusiness ? 'Business' : capitalize(tier);
  const monthlyTotal = getCreditsForTier(tier);
  const monthlyBalance = subscriptionStatus.creditBalance ?? 0;
  const showMonthly =
    !isBusiness &&
    monthlyTotal > 0 &&
    (subscriptionStatus.status === 'active' ||
      subscriptionStatus.status === 'past_due') &&
    subscriptionStatus.creditBalance !== undefined;
  const extraCredits = subscriptionStatus.extraCredits ?? 0;
  // Paid plans always show the pack balance (including 0). Free and business
  // accounts only show it once they actually hold extra credits.
  const showExtra = isBusiness ? extraCredits > 0 : isPaid || extraCredits > 0;
  // Subscriptions bought in the mobile app are billed by Apple/Google, not
  // Stripe — the web Customer Portal can't manage them.
  const isMobileSource =
    subscriptionStatus.source === 'apple' ||
    subscriptionStatus.source === 'google';

  const handleCancelScheduledChange = async () => {
    if (isLoading) return;

    const confirmed = await showConfirm(
      'Cancel Scheduled Change?',
      'Are you sure you want to cancel the scheduled plan change? Your current plan will continue unchanged.',
      {
        confirmText: 'Yes, Cancel Change',
        cancelText: 'Keep Change',
        confirmVariant: 'danger',
      }
    );

    if (!confirmed) return;

    try {
      setIsLoading(true);
      await trackButtonClick(source, 'Cancel Scheduled Change');

      const authResult = await instance.acquireTokenSilent({ ...loginRequest });
      const apiUrl = getApiHost();

      await axios.post(
        `${apiUrl}/user/cancel-scheduled-change`,
        {},
        {
          headers: { Authorization: `Bearer ${authResult.idToken}` },
        }
      );

      await showAlert(
        'Change Cancelled',
        'Your scheduled plan change has been cancelled. Your current plan will continue.'
      );
      window.location.reload();
    } catch (error) {
      console.error('Cancel scheduled change error:', error);
      await showAlert(
        'Error',
        'Something went wrong while cancelling the scheduled change. Please try again.'
      );
      setIsLoading(false);
    }
  };

  return (
    <div
      className={`
        w-full rounded-2xl border border-gray-200 bg-white p-6 shadow-sm
      `}
    >
      <DialogComponent />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-xl font-semibold text-gray-800">My Subscription</h3>
        <span
          className={`
            inline-flex items-center gap-1.5 rounded-full bg-yellow-100 px-3
            py-1 text-sm font-semibold text-yellow-700
          `}
        >
          {planLabel}
          {subscriptionStatus.billingPeriod && (
            <span className="font-normal text-yellow-600">
              ·{' '}
              {subscriptionStatus.billingPeriod === 'annual'
                ? 'Annual'
                : 'Monthly'}
            </span>
          )}
        </span>
      </div>

      {/* Payment Failure Warning */}
      {subscriptionStatus.status === 'past_due' && !isMobileSource && (
        <div className="mt-4 rounded-lg border border-red-300 bg-red-100 p-3">
          <p className="font-semibold text-red-700">
            ⚠️ Your payment failed. Please update your payment method in the
            Customer Portal to avoid losing your subscription.
          </p>
        </div>
      )}
      {subscriptionStatus.status === 'past_due' && isMobileSource && (
        <div className="mt-4 rounded-lg border border-red-300 bg-red-100 p-3">
          <p className="font-semibold text-red-700">
            ⚠️ Your payment failed. Please update your payment method in the{' '}
            {subscriptionStatus.source === 'apple'
              ? 'App Store'
              : 'Google Play Store'}{' '}
            to avoid losing your subscription.
          </p>
        </div>
      )}

      {/* Scheduled Plan Change Notice */}
      {subscriptionStatus.scheduledPlanChange && (
        <div
          className={`mt-4 rounded-lg border border-orange-300 bg-orange-50 p-3`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-2">
              <svg
                className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p className="font-medium text-orange-800">
                  Scheduled Plan Change
                </p>
                <p className="mt-1 text-sm text-orange-700">
                  Your plan will change to{' '}
                  <span className="font-semibold">
                    {capitalize(subscriptionStatus.scheduledPlanChange.newTier)}
                  </span>{' '}
                  (
                  {subscriptionStatus.scheduledPlanChange.newBillingPeriod ===
                  'annual'
                    ? 'Annual'
                    : 'Monthly'}
                  ) on{' '}
                  <span className="font-semibold">
                    {new Date(
                      subscriptionStatus.scheduledPlanChange.effectiveDate
                    ).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </p>
              </div>
            </div>
            <button
              onClick={handleCancelScheduledChange}
              disabled={isLoading}
              className={`
                flex-shrink-0 cursor-pointer rounded-md border border-orange-400
                bg-white px-3 py-1.5 text-xs font-medium text-orange-700
                transition-all duration-100
                hover:bg-orange-100
                active:scale-95
                disabled:cursor-not-allowed disabled:opacity-50
                sm:text-sm
              `}
            >
              Cancel Change
            </button>
          </div>
        </div>
      )}

      {showMonthly && (
        <MonthlyCreditBar balance={monthlyBalance} total={monthlyTotal} />
      )}

      {showExtra && (
        <div className="mt-5">
          <p
            className={`flex items-center justify-between text-sm text-gray-700`}
          >
            <span>Extra Credit Available</span>
            <span className="font-semibold text-gray-800">{extraCredits}</span>
          </p>
          <p className="mt-1 text-sm text-gray-400">
            These credits never expire.
          </p>
        </div>
      )}

      {isBusiness && (
        <BusinessApiLimits subscriptionStatus={subscriptionStatus} />
      )}

      {!isPaid && !isBusiness && (
        <p className="mt-4 text-sm text-gray-500">
          You&apos;re on the Free plan. Upgrade for more credits and unlimited
          projects.
        </p>
      )}

      {/* Actions */}
      {isMobileSource ? (
        <div className="mt-6 rounded-lg border border-blue-300 bg-blue-50 p-3">
          <p className="font-medium text-blue-800">
            You subscribed through the mobile app
          </p>
          <p className="mt-1 text-sm text-blue-700">
            Your subscription is billed by{' '}
            {subscriptionStatus.source === 'apple'
              ? 'the App Store'
              : 'Google Play'}
            . Please manage or change it in the Pineapple Music Lab mobile app.
          </p>
        </div>
      ) : (
        <div className="mt-6 flex flex-row flex-wrap items-center gap-3">
          {!isBusiness && (
            <Link
              to="/pricing"
              onClick={async () =>
                await trackButtonClick(source, 'Change Plan')
              }
              className={`
                inline-flex min-h-[44px] shrink-0 items-center justify-center
                rounded-full bg-gray-800 px-6 py-2 font-semibold text-white
                transition-transform duration-100
                hover:bg-gray-700
                active:scale-95
              `}
            >
              {isPaid ? 'Change plan' : 'Upgrade plan'}
            </Link>
          )}
          <CustomerPortal
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            variant="compact"
            label="Manage billing"
          />
        </div>
      )}
    </div>
  );
};
